import React, { useState, useEffect, useCallback } from 'react';
import { isAxiosError } from 'axios';
import { Badge, Button, Card, Spinner, StatusBadge } from '@/components/ui';
import { Form, type FormField } from '../../../services/formService';
import { Task } from '../../../types/Task';
import FormActionBar from './FormActionBar';
import FormSummaryView from './FormSummaryView';
import FormCompleteView from './FormCompleteView';
import HiredEquipmentDetailedView from '../../hired-equipment/components/HiredEquipmentDetailedView';
import { formUsesHeLineItemSummary } from '../../hired-equipment/utils/heSummaryRows';
import {
  findHe8HourRateValue,
  hiredEquipmentHourBreakdown,
  parseOvertimeEligibleFlag,
} from '../../hired-equipment/utils/overtime';
import FormHistoryModal from './FormHistoryModal';
import ResponsiveModal from '../../../components/ResponsiveModal';
import LabourOtFormBucketEditor from './LabourOtFormBucketEditor';
import { usePermissions } from '../../../hooks/usePermissions';
import formAttachmentService from '../../../services/formAttachmentService';
import { validateForm, FormValidationResult, getFormDisplayLabourHours, TASK_DURATION_VS_ALLOCATION_CODE } from '../utils/formValidation';
import { buildMechanicEffortSummaries, isMechanicDailyLog } from '../utils/mechanicDailyLog';
import { getFormEmployeeDisplayName } from '../utils/formDisplayName';
import {
  resolveLabourLineCostForFormTask,
  submissionHasPersistedLabourOvertimeHours,
  type FormLabourOvertimeAllocationRow,
  type PersistedLabourEffort,
} from '../../../utils/labourOvertimeDisplay';
import effortService from '../../../services/effortService';
import {
  formatHeApprovalSubtitle,
  heApprovalTitleParts,
  heEntryMethodFromForm,
  hePoNumberFromForm,
  heServiceAreaFromForm,
  heVendorNameFromForm,
  heWorkDateFromForm,
} from '../../hired-equipment/utils/approvalListRow';
import '../styles/FormList.css';

interface FormCardProps {
  form: Form;
  onApprove?: (formId: string, options?: { emailVendorPdf?: boolean; vendorEmail?: { to: string; subject: string; bodyText: string } }) => Promise<void>;
  onReject?: (formId: string, comment: string) => Promise<void>;
  onSaveField?: (formId: string, fieldId: number, value: string) => Promise<void>;
  onFieldsReplace?: (formId: string, fields: FormField[]) => void;
  onHeResubmitted?: (formId: string) => void | Promise<void>;
  availableTasks: Task[];
  tasksLoading: boolean;
  /** Labour rows from /efforts/labour-by-form-ids for OT-aware header totals. */
  persistedLabourEfforts?: PersistedLabourEffort[];
  /** Per-submission OT allocation rows (lines without labour Effort still get correct $/OT display). */
  labourOvertimeAllocations?: FormLabourOvertimeAllocationRow[];
  /** Refetch labour efforts + OT allocations after bucket PATCH. */
  onLabourOtAllocationsRefresh?: () => void | Promise<void>;
  onDecline?: (formId: string, comment: string) => Promise<void>;
  onWithdrawApproval?: (formId: string) => Promise<void>;
  onWithdrawRevisions?: (formId: string) => Promise<void>;
  onWithdrawDecline?: (formId: string) => Promise<void>;
  isProcessing?: boolean;
  processingAction?: string | null;
  isFading?: boolean;
  fadingAction?: string | null;
  defaultExpanded?: boolean;
}

const FormCard: React.FC<FormCardProps> = ({
  form,
  onApprove,
  onReject,
  onSaveField,
  onFieldsReplace,
  onHeResubmitted,
  availableTasks,
  tasksLoading,
  persistedLabourEfforts = [],
  labourOvertimeAllocations = [],
  onLabourOtAllocationsRefresh,
  onDecline,
  onWithdrawApproval,
  onWithdrawRevisions,
  onWithdrawDecline,
  isProcessing = false,
  processingAction = null,
  isFading = false,
  fadingAction = null,
  defaultExpanded = false
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // When navigating with ?formId=, parent may set defaultExpanded after form loads
  useEffect(() => {
    if (defaultExpanded) setIsExpanded(true);
  }, [defaultExpanded]);

  const [viewMode, setViewMode] = useState<'summary' | 'detailed'>('summary');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showLabourOtFormModal, setShowLabourOtFormModal] = useState(false);
  const [isEnsuringLabourOt, setIsEnsuringLabourOt] = useState(false);
  /** Fresh allocation rows for the OT modal (avoids stale props before parent re-render). */
  const [labourOtModalAllocations, setLabourOtModalAllocations] = useState<
    FormLabourOvertimeAllocationRow[] | null
  >(null);
  const { hasPermission } = usePermissions();
  const [validationResult, setValidationResult] = useState<FormValidationResult>({
    errors: [],
    warnings: [],
    hasErrors: false,
    hasWarnings: false
  });
  const [photoCount, setPhotoCount] = useState<number>(0);
  const [photoCountLoading, setPhotoCountLoading] = useState(true);
  const [hasBeenWithdrawn, setHasBeenWithdrawn] = useState(false);
  
  // Keep these for backward compatibility with FormActionBar
  const hasInvalidActivity = validationResult.errors.some(e => e.message.includes('999300'));
  const hasIneligibleTask = validationResult.errors.some(e => e.message.includes('status') && e.message.includes('cannot receive effort'));
  const hasTaskDurationAllocationConflict = validationResult.errors.some(
    (e) => e.code === TASK_DURATION_VS_ALLOCATION_CODE
  );
  const hasZeroHours = validationResult.warnings.some(w => w.taskGroupId === 'form' && w.message.includes('no hours entered'));

  // Function to check if form has been withdrawn before
  const checkIfFormHasBeenWithdrawn = async (formId: string) => {
    try {
      const response = await fetch(`/api/forms/${formId}/history`);
      if (response.ok) {
        const history = await response.json();
        const hasWithdrawal = history.some((entry: any) => 
          entry.stage === 'withdraw_approval' || entry.stage === 'withdraw_revisions' || entry.stage === 'approval_withdrawn' || entry.stage === 'revisions_withdrawn' || entry.stage === 'decline_withdrawn'
        );
        setHasBeenWithdrawn(hasWithdrawal);
      }
    } catch (error) {
      console.error('Error checking form withdrawal history:', error);
      setHasBeenWithdrawn(false);
    }
  };

  // Check if form has been withdrawn when component mounts
  useEffect(() => {
    checkIfFormHasBeenWithdrawn(form.id);
  }, [form.id]);

  // Validate form when tasks have finished loading (run even if availableTasks is empty so form-level checks like 0-hours warning still run)
  useEffect(() => {
    if (tasksLoading) {
      setValidationResult({
        errors: [],
        warnings: [],
        hasErrors: false,
        hasWarnings: false
      });
    } else {
      const result = validateForm(form, availableTasks, hasBeenWithdrawn, labourOvertimeAllocations);
      setValidationResult(result);
    }
  }, [availableTasks, tasksLoading, form.fields, form.status, hasBeenWithdrawn, labourOvertimeAllocations]);

  // Fetch photo count when component mounts
  useEffect(() => {
    const fetchPhotoCount = async () => {
      try {
        setPhotoCountLoading(true);
        const count = await formAttachmentService.getFormAttachmentCount(form.id);
        setPhotoCount(count);
      } catch (error) {
        console.error('Error fetching photo count:', error);
        setPhotoCount(0);
      } finally {
        setPhotoCountLoading(false);
      }
    };

    fetchPhotoCount();
  }, [form.id]);

  // Get header information
  const employeeName = getFormEmployeeDisplayName(form);
  const defaultForemanArea = form.fields.find(f => f.field === 'ForemanArea')?.value || '';
  const foremanArea = defaultForemanArea;

  // Determine if it's a Hired Equipment form or Daily Log
  const isHiredEquipmentForm = form.fields.some(field =>
    field.path.includes('EquipmentTimeWorkedJ') ||
    field.field === 'AmountBilled'
  );

  const EDITABLE_SUBMISSION_STATUSES = ['pending', 'revisions completed'];
  const canAdjustLabourOtBuckets =
    EDITABLE_SUBMISSION_STATUSES.includes((form.status || '').toLowerCase()) &&
    hasPermission('form:adjust_labour_ot_buckets');
  const LABOUR_OT_EPS = 1e-4;
  const hasLabourOtAllocationLines = labourOvertimeAllocations.some((a) => {
    const input = Number(a.inputHours ?? 0);
    const straight = Number(a.labourStraightHours ?? 0);
    const o15 = Number(a.labourOvertime15Hours ?? 0);
    const o20 = Number(a.labourOvertime20Hours ?? 0);
    return (
      a.id != null &&
      (input > LABOUR_OT_EPS || straight + o15 + o20 > LABOUR_OT_EPS)
    );
  });
  const hasFormLabourHoursForOt = getFormDisplayLabourHours(form) > LABOUR_OT_EPS;
  const showEditLabourOtBucketsInBar =
    canAdjustLabourOtBuckets &&
    (hasLabourOtAllocationLines || hasFormLabourHoursForOt) &&
    !isHiredEquipmentForm;

  const handleEditLabourOtBucketsOpen = useCallback(async () => {
    setIsEnsuringLabourOt(true);
    try {
      await effortService.ensureLabourOtAllocationsForForm(form.id);
      const fresh = await effortService.getLabourOvertimeAllocationsByFormIds([String(form.id)]);
      await onLabourOtAllocationsRefresh?.();

      const active = fresh.filter(
        (a) => a.id != null && Number(a.inputHours ?? 0) > LABOUR_OT_EPS
      );
      if (active.length === 0) {
        alert(
          'Could not load labour OT rows for editing. This submission may have no allocatable labour hours, or allocation data may still be syncing — refresh the page and try again.'
        );
        return;
      }

      setLabourOtModalAllocations(fresh);
      setShowLabourOtFormModal(true);
    } catch (e) {
      let message = 'Could not prepare labour OT rows for editing.';
      if (isAxiosError(e)) {
        const errBody = e.response?.data as { error?: string } | undefined;
        if (errBody && typeof errBody.error === 'string') {
          message = errBody.error;
        } else if (e.message) {
          message = e.message;
        }
      } else if (e instanceof Error && e.message) {
        message = e.message;
      }
      alert(message);
    } finally {
      setIsEnsuringLabourOt(false);
    }
  }, [form.id, onLabourOtAllocationsRefresh]);
  
  // Extract SubcontractorOrHERep field value to use in subtitle
  const subcontractorOrHERep = form.fields.find(f => 
    f.field === 'SubcontractorOrHERep' || 
    f.path.includes('SubcontractorOrHERep')
  )?.value;
  
  // Use SubcontractorOrHERep value if available, otherwise use default form type
  // Handle array values (the field might have values as an array)
  let subcontractorTypeValue = subcontractorOrHERep;
  if (Array.isArray(subcontractorOrHERep)) {
    subcontractorTypeValue = subcontractorOrHERep[0];
  }
  const displayType = subcontractorTypeValue && String(subcontractorTypeValue).trim() 
    ? String(subcontractorTypeValue).trim() 
    : (
      isHiredEquipmentForm
        ? 'Hired Equipment'
        : isMechanicDailyLog(form)
          ? 'Daily Log - Mechanic'
          : 'Daily Log - RB'
    );
  
  const formType = displayType;

  // Choose the correct date field for the header (always use ActualStartTime)
  let headerDate: string = form.fields.find(f => f.field === 'ActualStartTime')?.value || form.createdAt;

  // Format the date safely (no timezone conversion for YYYY-MM-DD)
  let formattedDate = headerDate;
  if (typeof headerDate === 'string' && /\d{4}-\d{2}-\d{2}$/.test(headerDate)) {
    formattedDate = headerDate;
  } else {
    try {
      formattedDate = new Date(headerDate).toLocaleDateString();
    } catch {
      formattedDate = headerDate;
    }
  }

  // Get SubcontractorNameCom for the title
  const subcontractorNameCom = form.fields.find(f => 
    f.path.includes('SubcontractorNameCom') || f.field === 'SubcontractorNameCom'
  )?.value;

  // --- Begin: Accurate cost and hours calculation (from FormSummaryView) ---
  // Group fields by task
  const taskGroups = form.fields.reduce((groups: { [key: string]: Form['fields'] }, field) => {
    if (!field.path.includes('WorkCompleted')) return groups;
    const taskMatch = field.path.match(/WorkCompleted\.Tasks (\d+\.\d+)/);
    if (!taskMatch) return groups;
    const taskGroupId = taskMatch[1];
    if (!groups[taskGroupId]) {
      groups[taskGroupId] = [];
    }
    groups[taskGroupId].push(field);
    return groups;
  }, {});

  const isMechanicFormForTotals = !isHiredEquipmentForm && isMechanicDailyLog(form);

  // Process each task group for efforts
  const safeParseFloat = (value: string | undefined, defaultValue: number = 0): number => {
    if (!value) return defaultValue;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  const taskEfforts = isMechanicFormForTotals
    ? (() => {
        const detailEfforts = buildMechanicEffortSummaries(form);
        const simplified = detailEfforts.map((e) => ({
          type: e.type,
          quantity: e.quantity,
          totalCost: e.totalCost,
          unitOfMeasure: e.unitOfMeasure
        }));
        const totalCost = simplified.reduce((s, e) => s + e.totalCost, 0);
        return [{ taskId: 'mechanic', efforts: simplified, totalCost }];
      })()
    : Object.entries(taskGroups).map(([taskGroupId, fields]) => {
    let efforts: { type: string; quantity: number; totalCost: number; unitOfMeasure: string }[] = [];
    const taskIdField = fields.find(f => f.field === 'TaskId' || f.path?.match(/\.TaskId$/));
    const taskFieldForId = fields.find(f => f.field === 'Task' && f.path?.endsWith('.Task'));
    const workCompletedId = fields.find(f => f.field === 'WorkCompletedID')?.value;
    const rawTaskId = taskIdField?.value || taskFieldForId?.value || workCompletedId || '0';
    const taskNumber = parseInt(String(rawTaskId), 10) || 0;
    let taskNameForLabour = fields.find(f => f.field === 'TaskName')?.value || '';
    if (taskNumber) {
      const t = availableTasks.find(x => x.id === taskNumber);
      if (t) {
        taskNameForLabour = t.name || t.description || taskNameForLabour;
      }
    }
    // Labour
    const labourHours = fields.find(f => f.field === 'TaskDurationHours' && f.path.includes('Timecard and Job C'))?.value || '0';
    const labourRate = fields.find(f => f.field === 'ClassRate')?.value;
    const taskAllocRows = labourOvertimeAllocations.filter((a) => a.originalTaskId === taskNumber);
    const hasTaskAllocationRows = taskAllocRows.length > 0;
    const qty = safeParseFloat(labourHours);
    const lineKey =
      taskNumber > 0 ? `WorkCompleted.Tasks ${taskGroupId}|task=${taskNumber}` : undefined;
    const labourMerge = {
      taskName: taskNameForLabour,
      persistedLabourForForm: persistedLabourEfforts,
      labourOvertimeAllocations,
      lineKey,
    };
    if (qty > 0) {
      const rate = labourRate ? safeParseFloat(labourRate) : 0;
      const fallbackCost = rate * qty;
      const labourCost = hasTaskAllocationRows
        ? resolveLabourLineCostForFormTask(
            form.id,
            taskNumber,
            availableTasks,
            fallbackCost,
            labourMerge
          )
        : fallbackCost;
      efforts.push({
        type: 'Labour',
        quantity: qty,
        totalCost: labourCost,
        unitOfMeasure: 'Hours',
      });
    }
    // Subcontractor - use same comprehensive logic as FormSummaryView
    const hasTimeWorkedJobCostFields = fields.some(f => f.path.includes('TimeWorkedJobCost'));
    
    if (hasTimeWorkedJobCostFields) {
      // Group TimeWorkedJobCost fields by their equipment group (e.g., "0", "1", etc.)
      // Capture ALL fields under each equipment group, including nested sections like CostDetails, Attachments, etc.
      const equipmentGroups: { [key: string]: Form['fields'] } = {};
      
      fields.forEach(field => {
        if (field.path.includes('TimeWorkedJobCost 1.')) {
          // Extract equipment group from path (e.g., "0" from "TimeWorkedJobCost 1.0.EquipmentTimeWorkedJ...")
          const equipmentMatch = field.path.match(/TimeWorkedJobCost 1\.(\d+)\./);
          if (equipmentMatch) {
            const equipmentGroupId = equipmentMatch[1];
            if (!equipmentGroups[equipmentGroupId]) {
              equipmentGroups[equipmentGroupId] = [];
            }
            equipmentGroups[equipmentGroupId].push(field);
          }
        }
      });

      // Process each equipment group with breakdown
      Object.entries(equipmentGroups).forEach(([equipmentGroupId, equipmentFields]) => {
        // Extract breakdown fields
        const hoursLabour = parseFloat(equipmentFields.find(f => f.field === 'HoursLabour')?.value || '0');
        const rateBlueBook = parseFloat(
          equipmentFields.find(f => f.field === 'RateBlueBook')?.value ||
          equipmentFields.find(f => f.field === 'RateBlueBookPrim')?.value ||
          equipmentFields.find(f => f.field === 'RateBlueBookSec')?.value || '0'
        );
        const fuelPriceAdjustment = parseFloat(
          equipmentFields.find(f => f.field === 'FuelPriceAdjustment')?.value ||
          equipmentFields.find(f => f.field === 'FuelPriceAdjustmentS')?.value ||
          equipmentFields.find(f => f.field === 'PrimaryFuelPriceAdju')?.value || '0'
        );
        const overtime8HourRate = parseFloat(
          findHe8HourRateValue(equipmentFields) ||
          equipmentFields.find(f => f.field === 'Overtime8HourRate')?.value ||
          equipmentFields.find(f => f.field === 'OvertimeRate8to12')?.value ||
          equipmentFields.find(f => f.field === '8HourRate')?.value || '0'
        );
        const overtime12HourRate = parseFloat(
          equipmentFields.find(f => f.field === 'Overtime12HourRate')?.value ||
          equipmentFields.find(f => f.field === 'OvertimeRateAbove12')?.value ||
          equipmentFields.find(f => f.field === '12HourAdditionalRate')?.value || '0'
        );
        const overtimeEligible = parseOvertimeEligibleFlag(
          equipmentFields.find(f => f.field === 'OvertimeEligible')?.value,
        );
        const breakdown = hiredEquipmentHourBreakdown({
          overtimeEligible,
          hoursLabour,
          rateBlueBook,
          overtime8HourRate,
          overtime12HourRate,
        });
        
        // Use AttachmentTotal - should now be captured in equipmentFields
        let attachmentTotal = parseFloat(equipmentFields.find(f => f.field === 'AttachmentTotal')?.value || '0');
        
        // Get CalculatedCost for fallback calculation
        const calculatedCostField = equipmentFields.find(f => f.field === 'CalculatedCost' || f.path.includes('CalculatedCost.CalculatedCost'));
        const calculatedCost = calculatedCostField ? parseFloat(calculatedCostField.value || '0') : 0;
        
        const fuelTotal = hoursLabour * fuelPriceAdjustment;
        const overtime812Total = overtimeEligible
          ? breakdown.ot15Cost
          : (hoursLabour > 8 && hoursLabour <= 12) ? (hoursLabour - 8) * overtime8HourRate : 0;
        const overtimeAbove12Total = overtimeEligible
          ? breakdown.ot20Cost
          : hoursLabour > 12 ? (hoursLabour - 12) * overtime12HourRate : 0;
        
        if (attachmentTotal === 0 && calculatedCost > 0 && hoursLabour > 0) {
          const hoursCost = overtimeEligible
            ? breakdown.regularQty * (rateBlueBook + fuelPriceAdjustment)
              + breakdown.ot15Qty * (overtime8HourRate + fuelPriceAdjustment)
              + breakdown.ot20Qty * (overtime12HourRate + fuelPriceAdjustment)
            : hoursLabour * (rateBlueBook + fuelPriceAdjustment);
          attachmentTotal = overtimeEligible
            ? calculatedCost - hoursCost
            : calculatedCost - hoursCost - overtime812Total - overtimeAbove12Total;
          if (attachmentTotal < 0) attachmentTotal = 0;
        }

        const hasBreakdown = hoursLabour > 0 && rateBlueBook > 0;
        const hasAttachments = attachmentTotal > 0;

        if (hasBreakdown) {
          efforts.push({
            type: 'Subcontractor',
            quantity: breakdown.regularQty,
            totalCost: breakdown.regularCost,
            unitOfMeasure: 'Hours',
          });
          
          if (breakdown.ot15Qty > 0 && overtime8HourRate > 0) {
            efforts.push({
              type: 'Subcontractor',
              quantity: breakdown.ot15Qty,
              totalCost: breakdown.ot15Cost,
              unitOfMeasure: 'Hours',
            });
          }
          
          if (breakdown.ot20Qty > 0 && overtime12HourRate > 0) {
            efforts.push({
              type: 'Subcontractor',
              quantity: breakdown.ot20Qty,
              totalCost: breakdown.ot20Cost,
              unitOfMeasure: 'Hours',
            });
          }
          
          // Fuel Surcharge
          if (fuelPriceAdjustment > 0) {
            efforts.push({
              type: 'Subcontractor',
              quantity: hoursLabour,
              totalCost: hoursLabour * fuelPriceAdjustment,
              unitOfMeasure: 'Hours',
            });
          }
        }
        
        // Attachments (INDEPENDENT of hours)
        if (hasAttachments) {
          efforts.push({
            type: 'Subcontractor',
            quantity: 1,
            totalCost: attachmentTotal,
            unitOfMeasure: 'Lump Sum',
          });
        }
        
        // If no breakdown data at all, use fallback
        if (!hasBreakdown && !hasAttachments) {
          const calculatedCostFallback = equipmentFields.find(f => 
            f.path.includes('CalculatedCost.CalculatedCost')
          )?.value;
          const amountBilled = form.fields.find(f => 
            f.path.includes('WorkCompletedCosting.AmountBilled') &&
            f.field === 'AmountBilled'
          )?.value;

          let subcontractorCost = 0;
          if (calculatedCostFallback && parseFloat(calculatedCostFallback) > 0) {
            subcontractorCost = parseFloat(calculatedCostFallback);
          } else if (amountBilled && parseFloat(amountBilled) > 0) {
            subcontractorCost = parseFloat(amountBilled);
          }

          if (subcontractorCost > 0) {
            efforts.push({
              type: 'Subcontractor',
              quantity: 1,
              totalCost: subcontractorCost,
              unitOfMeasure: 'Hours',
            });
          }
        }
      });
    } else {
      // Original subcontractor logic for non-equipment forms
      // Find calculated cost and subcontractor name using the specific paths
      const calculatedCost = fields.find(f => 
        f.path.includes('CalculatedCost.CalculatedCost')
      )?.value;

      // Also look for AmountBilled in WorkCompletedCosting section
      const amountBilled = form.fields.find(f => 
        f.path.includes('WorkCompletedCosting.AmountBilled') &&
        f.field === 'AmountBilled'
      )?.value;

      // Determine which cost to use - prefer CalculatedCost, fallback to AmountBilled
      let subcontractorCost = 0;
      if (calculatedCost && parseFloat(calculatedCost) > 0) {
        subcontractorCost = parseFloat(calculatedCost);
      } else if (amountBilled && parseFloat(amountBilled) > 0) {
        subcontractorCost = parseFloat(amountBilled);
      }

      // === Robust Subcontractor Name Extraction (matching FormSummaryView logic) ===
      const possibleSubNames = [
        // Look in task-specific fields first
        fields.find(f => f.field === 'SubcontractorNameCom')?.value,
        fields.find(f => f.path && f.path.toLowerCase().includes('subcontractornamecom'))?.value,
        fields.find(f => f.path.includes('SubcontractorInforma.SubcontractorNameCom'))?.value,
        fields.find(f => f.path.includes('SubcontractorName'))?.value,
        fields.find(f => f.field === 'SubcontractorName')?.value,
        fields.find(f => f.path && f.path.toLowerCase().includes('subcontractorname'))?.value,
        fields.find(f => f.field && f.field.startsWith('SubcontractorName'))?.value,
        fields.find(f => f.path && f.path.toLowerCase().includes('subcontractorname'))?.value,
        // Then look in main form fields as fallback
        form.fields.find(f => f.field === 'SubcontractorNameCom')?.value,
        form.fields.find(f => f.path && f.path.toLowerCase().includes('subcontractornamecom'))?.value,
        form.fields.find(f => f.path.includes('SubcontractorInforma.SubcontractorNameCom'))?.value,
        form.fields.find(f => f.field === 'SubcontractorName')?.value,
        form.fields.find(f => f.path && f.path.toLowerCase().includes('subcontractorname'))?.value,
        form.fields.find(f => f.field && f.field.startsWith('SubcontractorName'))?.value,
        form.fields.find(f => f.path && f.path.toLowerCase().includes('subcontractorname'))?.value
      ];
      const subcontractorNameFinal = possibleSubNames.find(v => v && v.trim());

      // Extract real values from form (same logic as FormSummaryView)
      let subcontractorQuantity = 1;
      let subcontractorRate = 0;
      
      // Look for quantity in any field that might contain hours/time/quantity
      for (const field of fields) {
        const value = parseFloat(field.value);
        if (!isNaN(value) && value > 0) {
          if (!field.field.toLowerCase().includes('calculated') && 
              !field.field.toLowerCase().includes('total') &&
              (field.field.toLowerCase().includes('hour') || 
               field.field.toLowerCase().includes('time') ||
               field.field.toLowerCase().includes('duration') ||
               field.field.toLowerCase().includes('quantity'))) {
            subcontractorQuantity = value;
            break;
          }
        }
      }
      
      // Look for rate in any field that might contain rate/price per unit
      for (const field of fields) {
        const value = parseFloat(field.value);
        if (!isNaN(value) && value > 0) {
          if (!field.field.toLowerCase().includes('calculated') && 
              !field.field.toLowerCase().includes('total') &&
              (field.field.toLowerCase().includes('rate') || 
               field.field.toLowerCase().includes('price') ||
               field.field.toLowerCase().includes('cost'))) {
            subcontractorRate = value;
            break;
          }
        }
      }
      
      const subcontractorUnitOfMeasure =
        fields.find(f => f.field === 'UnitsMeasurement 4')?.value ||
        form.fields.find(f => f.field === 'UnitsMeasurement 4')?.value ||
        fields.find(f => f.field === 'UnitsMeasurementPrim')?.value ||
        form.fields.find(f => f.field === 'UnitsMeasurementPrim')?.value ||
        fields.find(f => f.field === 'UnitsMeasurement')?.value ||
        form.fields.find(f => f.field === 'UnitsMeasurement')?.value ||
        'Hours';

      if (subcontractorCost > 0) {
        // Use the same detailed breakdown logic as FormSummaryView
        // This ensures the title cost matches the Grand Total
        const hasTimeWorkedJobCostFields = fields.some(f => 
          f.path.includes('TimeWorkedJobCost') && 
          f.path.includes('EquipmentTimeWorkedJ')
        );
        
        if (hasTimeWorkedJobCostFields) {
          // Use the same equipment grouping logic as FormSummaryView
          const equipmentGroups: { [key: string]: any[] } = {};
          
          fields.forEach((field: any) => {
            if (field.path.includes('TimeWorkedJobCost 1.')) {
              const equipmentMatch = field.path.match(/TimeWorkedJobCost 1\.(\d+)\./);
              if (equipmentMatch) {
                const equipmentGroupId = equipmentMatch[1];
                if (!equipmentGroups[equipmentGroupId]) {
                  equipmentGroups[equipmentGroupId] = [];
                }
                equipmentGroups[equipmentGroupId].push(field);
              }
            }
          });
          
          // Process each equipment group with the same logic as FormSummaryView
          Object.entries(equipmentGroups).forEach(([equipmentGroupId, equipmentFields]) => {
            // Extract the same fields as FormSummaryView
            const hoursLabour = parseFloat(equipmentFields.find(f => f.field === 'HoursLabour')?.value || '0');
            const rateBlueBook = parseFloat(
              equipmentFields.find(f => f.field === 'RateBlueBook')?.value ||
              equipmentFields.find(f => f.field === 'RateBlueBookPrim')?.value ||
              equipmentFields.find(f => f.field === 'RateBlueBookSec')?.value || '0'
            );
            const fuelPriceAdjustment = parseFloat(
              equipmentFields.find(f => f.field === 'FuelPriceAdjustment')?.value ||
              equipmentFields.find(f => f.field === 'FuelPriceAdjustmentS')?.value ||
              equipmentFields.find(f => f.field === 'PrimaryFuelPriceAdju')?.value || '0'
            );
            const attachmentTotal = parseFloat(equipmentFields.find(f => f.field === 'AttachmentTotal')?.value || '0');
            const calculatedCost = parseFloat(equipmentFields.find(f => f.field === 'CalculatedCost')?.value || '0');
            
            const equipmentName = equipmentFields.find(f => f.field === 'SubcontractorName')?.value || 'Equipment';
            
            // Create the same efforts as FormSummaryView
            const hasHours = hoursLabour > 0 && rateBlueBook > 0;
            const hasAttachments = attachmentTotal > 0;
            
            if (hasHours) {
              // Regular Hours
              efforts.push({
                type: 'Subcontractor',
                quantity: hoursLabour,
                totalCost: hoursLabour * rateBlueBook,
                unitOfMeasure: 'Hours',
              });
              
              // Fuel Surcharge
              if (fuelPriceAdjustment > 0) {
                efforts.push({
                  type: 'Subcontractor',
                  quantity: hoursLabour,
                  totalCost: hoursLabour * fuelPriceAdjustment,
                  unitOfMeasure: 'Hours',
                });
              }
            }
            
            // Attachments
            if (hasAttachments) {
              efforts.push({
                type: 'Subcontractor',
                quantity: 1,
                totalCost: attachmentTotal,
                unitOfMeasure: 'Lump Sum',
              });
            }
          });
        } else {
          // Fallback to simple calculation for non-equipment forms
          const finalQuantity = subcontractorQuantity > 0 ? subcontractorQuantity : 1;
          const finalRate = subcontractorRate > 0 ? subcontractorRate : subcontractorCost;
          const calculatedTotal = finalQuantity * finalRate;
          const finalCost = calculatedTotal > 0 ? calculatedTotal : subcontractorCost;
          
          efforts.push({
            type: 'Subcontractor',
            quantity: finalQuantity,
            totalCost: finalCost,
            unitOfMeasure: subcontractorUnitOfMeasure,
          });
        }
      }
    }
    // Primary Vehicle
    const primaryVehicleNumber = fields.find(f => f.field === 'OtherVehicleDescri 1')?.value || 
                                  fields.find(f => f.field === 'VehicleUnitNumber 1')?.value;
    const primaryVehicleRate = fields.find(f => f.field === 'VehicleRate')?.value;
    const primaryVehicleHours = fields.find(f => f.field === 'HoursVehicleUsage')?.value;
    if (primaryVehicleNumber && primaryVehicleHours) {
      efforts.push({
        type: 'Vehicle',
        quantity: safeParseFloat(primaryVehicleHours),
        totalCost: safeParseFloat(primaryVehicleHours) * safeParseFloat(primaryVehicleRate, 1),
        unitOfMeasure: 'Hours',
      });
    }
    // Secondary Vehicle
    const secondaryVehicleNumber = fields.find(f => f.field === 'OtherVehicleDescri 2')?.value || 
                                    fields.find(f => f.field === 'SecondaryUnitNumber')?.value;
    const secondaryVehicleRate = fields.find(f => f.field === 'SecondaryVehicleRate')?.value;
    const secondaryVehicleHours = fields.find(f => f.field === 'Hours Secondary Vehi')?.value;
    if (secondaryVehicleNumber && secondaryVehicleHours) {
      efforts.push({
        type: 'Vehicle',
        quantity: safeParseFloat(secondaryVehicleHours),
        totalCost: safeParseFloat(secondaryVehicleHours) * safeParseFloat(secondaryVehicleRate, 1),
        unitOfMeasure: 'Hours',
      });
    }
    // Materials
    const materials = fields.filter(f => f.field === 'MaterialUsed 4');
    materials.forEach(material => {
      const materialPath = material.path;
      const basePath = materialPath.substring(0, materialPath.lastIndexOf('.'));
      const quantity = fields.find(f => f.path.startsWith(basePath) && f.field === 'QuantityUsed 1')?.value;
      const rate = fields.find(f => f.path.startsWith(basePath) && f.field === 'MaterialRate')?.value || '0';
      const unitOfMeasure = fields.find(f => f.path.startsWith(basePath) && f.field === 'UnitOfMeasure')?.value || 'Units';
      if (quantity) {
        efforts.push({
          type: 'Material',
          quantity: safeParseFloat(quantity),
          totalCost: safeParseFloat(quantity) * safeParseFloat(rate, 1),
          unitOfMeasure,
        });
      }
    });
    // Meal
    const mealDurationMinutes = fields.find(f => f.field === 'MealDurationMinutes')?.value;
    if (mealDurationMinutes && parseFloat(mealDurationMinutes) > 0) {
      const mealDurationHours = parseFloat(mealDurationMinutes) / 60;
      efforts.push({
        type: 'Meal',
        quantity: mealDurationHours,
        totalCost: 0,
        unitOfMeasure: 'Hours',
      });
    }
    const totalCost = efforts.reduce((sum, effort) => sum + effort.totalCost, 0);
    return { taskId: taskGroupId, efforts, totalCost };
  });

  // Group by type
  const groupedEfforts = taskEfforts.flatMap(task => task.efforts).reduce((groups, effort) => {
    if (!groups[effort.type]) {
      groups[effort.type] = [];
    }
    groups[effort.type].push(effort);
    return groups;
  }, {} as { [key: string]: { type: string; quantity: number; totalCost: number; unitOfMeasure: string }[] });

  // Calculate totals
  const totalsByType = Object.entries(groupedEfforts).reduce((totals, [type, efforts]) => {
    totals[type] = {
      quantity: efforts.reduce((sum, e) => sum + e.quantity, 0),
      cost: efforts.reduce((sum, e) => sum + e.totalCost, 0),
      unitOfMeasure: efforts[0].unitOfMeasure
    };
    return totals;
  }, {} as { [key: string]: { quantity: number, cost: number, unitOfMeasure: string } });

  // Use the same detailed calculation logic as FormSummaryView to ensure title always matches Grand Total
  // This ensures the title shows the correct cost even when the submission is collapsed
  const grandTotal = Object.values(totalsByType).reduce((sum, type) => sum + type.cost, 0);
  let totalLabourHours = totalsByType['Labour']?.quantity || 0;
  let titleCostTotal = grandTotal;

  if (
    isMechanicFormForTotals &&
    totalLabourHours === 0 &&
    titleCostTotal === 0
  ) {
    const decRaw = form.fields.find((f) => f.field === 'TotalLabourTimeDec')?.value;
    const rateRaw =
      form.fields.find((f) => f.field === 'ClassRate')?.value ??
      form.fields.find((f) => f.field === 'Rate')?.value;
    const dec = parseFloat(String(decRaw || '0').replace(/[^0-9.-]/g, '')) || 0;
    const rate = parseFloat(String(rateRaw || '0').replace(/[^0-9.-]/g, '')) || 0;
    if (dec > 0) {
      totalLabourHours = dec;
      titleCostTotal = dec * rate;
    }
  }

  // Debug: Log the cost calculation to ensure it matches FormSummaryView
  console.log('FormCard cost calculation (using same logic as FormSummaryView):', {
    grandTotal,
    totalLabourHours,
    totalsByType,
    allEfforts: taskEfforts.flatMap(task => task.efforts).map(e => ({
      type: e.type,
      totalCost: e.totalCost
    })),
    sumOfAllEfforts: taskEfforts.flatMap(task => task.efforts).reduce((sum, e) => sum + e.totalCost, 0)
  });
  // --- End: Accurate cost and hours calculation ---

  // Header display parts — name is the primary line; hours & cost render as aligned
  // tabular stats beneath it (was one run-on "Name | Hours: X | Cost: $Y" string).
  // Hired Equipment rows match the Approvals mockup: Vendor — G. Miller (Role) · $X
  // with "By Purchase Order | SA 22 | Jul 10, 2026" underneath (no Hours/Cost line).
  let primaryName: string;
  if (isHiredEquipmentForm && subcontractorNameCom) {
    primaryName = `${subcontractorNameCom} - ${employeeName}`;
  } else if (subcontractorNameCom) {
    primaryName = `${employeeName} - ${subcontractorNameCom}`;
  } else {
    primaryName = employeeName;
  }
  const hoursText = totalLabourHours.toFixed(2);
  const costText = `$${titleCostTotal.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  const heTitle = isHiredEquipmentForm
    ? heApprovalTitleParts({
        vendorName: heVendorNameFromForm(form.fields) || subcontractorNameCom,
        submitterName: form.statusRequestedBy?.name,
        submitterLastName: form.statusRequestedBy?.lastName,
        roleName: form.statusRequestedBy?.role?.name,
        total: titleCostTotal,
      })
    : null;

  const heSubtitle = isHiredEquipmentForm
    ? formatHeApprovalSubtitle({
        entryMethod: heEntryMethodFromForm(form.fields),
        poNumber: hePoNumberFromForm(form.fields),
        serviceAreaLabel: heServiceAreaFromForm(form.fields),
        workDate: heWorkDateFromForm(form.fields) || headerDate,
      })
    : '';

  // Create subtitle
  let subtitle = `${formType} | ${foremanArea} | ${formattedDate}`;
  
  // Get start time for the subtitle
  const startTime = form.fields.find(f => f.field === 'ActualStartTime')?.value;
  if (!isHiredEquipmentForm && startTime) {
    try {
      const startTimeDate = new Date(startTime);
      if (!isNaN(startTimeDate.getTime())) {
        const hours = String(startTimeDate.getHours()).padStart(2, '0');
        const minutes = String(startTimeDate.getMinutes()).padStart(2, '0');
        const startTimeFormatted = `${hours}:${minutes}`;
        subtitle += ` | Start Time: ${startTimeFormatted}`;
      }
    } catch (error) {
      console.error('Error formatting start time:', error);
    }
  }

  // Shared left side of the collapsed header (used by both loading + loaded states):
  // name, labeled tabular Hours/Cost stats, meta subtitle, and any status comment.
  const headerInfo = (
    <div className="tw-min-w-0">
      <h5 className="tw-mb-0 tw-text-base tw-leading-snug tw-font-medium tw-text-content">
        {heTitle ? (
          <>
            {heTitle.lead}
            <span className="tw-mx-1.5 tw-font-normal tw-text-content-muted">·</span>
            <span className="tw-inline-flex tw-rounded tw-bg-accent-soft tw-px-1.5 tw-py-0 tw-font-display tw-text-sm tw-font-semibold tw-tabular-nums tw-leading-5 tw-text-accent-dark">
              {heTitle.cost}
            </span>
          </>
        ) : (
          primaryName
        )}
      </h5>
      {!isHiredEquipmentForm && (
        <div className="tw-mt-0.5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-text-xs">
          <span className="tw-text-content-secondary">
            Hours <span className="tw-font-semibold tw-tabular-nums tw-text-ink">{hoursText}</span>
          </span>
          <span className="tw-text-content-secondary">
            Cost <span className="tw-font-semibold tw-tabular-nums tw-text-ink">{costText}</span>
          </span>
        </div>
      )}
      <small className="tw-mt-0.5 tw-block tw-text-xs tw-text-content-muted">
        {isHiredEquipmentForm ? heSubtitle : subtitle}
      </small>
      {form.statusComments && (
        <small className={`tw-block tw-mt-0.5 tw-text-xs ${form.status === 'revisions completed' ? 'tw-text-success' : 'tw-text-danger'}`}>
          <i className={`bi bi-${form.status === 'revisions completed' ? 'check-circle' : 'exclamation-circle'} tw-mr-1`}></i>
          {form.statusComments}
        </small>
      )}
    </div>
  );

  // Remove getStatusBadgeClass and use unified badge rendering
  const getStatusBadge = (status: string) => {
    // Colour comes from the shared map's `submission` vocabulary (utils/statusUtils) rather
    // than a local table — this was one of twelve that had drifted apart. The wording stays
    // here because it is specific to this surface: "revisions completed" is deliberately shown
    // to the submitter as "Pending", since from their side the work is back in the queue.
    const displayStatus = (status === 'revisions completed')
      ? 'Pending'
      : status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';
    return (
      <StatusBadge
        status={status}
        vocabulary="submission"
        label={displayStatus}
        pill
        className="tw-capitalize tw-px-2 tw-font-semibold"
      />
    );
  };

  const handleExpandToggle = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleHistoryComment = async (updatedForm: Form) => {
    // Keep modal open after adding comment; parent can refresh form state if needed
  };

  // Implement withdraw handlers
  const handleWithdrawApproval = async (formId: string) => {
    if (onWithdrawApproval) {
      await onWithdrawApproval(formId);
    }
  };

  const handleWithdrawRevisions = async (formId: string) => {
    if (onWithdrawRevisions) {
      await onWithdrawRevisions(formId);
    }
  };

  const handleWithdrawDecline = async (formId: string) => {
    if (onWithdrawDecline) {
      await onWithdrawDecline(formId);
    }
  };

  // Add decline handler
  const handleDecline = async (formId: string, comment: string) => {
    if (onDecline) {
      await onDecline(formId, comment);
    }
  };

  /** Only after tasks/labour batch load to avoid showing OT from stale props. */
  const showSubmissionOtBadge =
    !tasksLoading &&
    submissionHasPersistedLabourOvertimeHours(labourOvertimeAllocations, persistedLabourEfforts);

  return (
    <>
      <Card className="tw-mb-2">
        <Card.Header className={`tw-px-3 tw-py-2 ${isExpanded ? '' : 'tw-border-b-0'}`}>
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
            <button
              type="button"
              className="tw-min-w-0 tw-flex-1 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-left"
              onClick={handleExpandToggle}
              aria-expanded={isExpanded}
            >
              {headerInfo}
            </button>
            <div className="tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
              {getStatusBadge(form.status || 'pending')}
              {showSubmissionOtBadge && !isHiredEquipmentForm && (
                <span
                  className="tw-inline-block tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-leading-none tw-rounded tw-bg-overtime tw-text-white"
                  title="This submission has DRM overtime (1.5× or 2× hours)"
                >
                  OT
                </span>
              )}
              {validationResult.hasErrors && (
                <Badge
                  bg="danger"
                  className="tw-inline-flex tw-items-center tw-gap-1"
                  title={`${validationResult.errors.length} error${validationResult.errors.length > 1 ? 's' : ''} found`}
                >
                  <i className="bi bi-exclamation-triangle-fill"></i>
                  <span>{validationResult.errors.length}</span>
                </Badge>
              )}
              {validationResult.hasWarnings && (
                <Badge
                  bg="warning"
                  className="tw-inline-flex tw-items-center tw-gap-1"
                  title={`${validationResult.warnings.length} warning${validationResult.warnings.length > 1 ? 's' : ''} found`}
                >
                  <i className="bi bi-exclamation-triangle-fill"></i>
                  <span>{validationResult.warnings.length}</span>
                </Badge>
              )}
              {!photoCountLoading && photoCount > 0 && (
                <Badge
                  bg="info"
                  text="white"
                  className="tw-inline-flex tw-items-center tw-gap-1"
                  title={`${photoCount} photo${photoCount > 1 ? 's' : ''} attached`}
                >
                  <i className="bi bi-camera-fill"></i>
                  <span>{photoCount}</span>
                </Badge>
              )}
              {photoCountLoading && (
                <Badge bg="light" className="tw-inline-flex tw-items-center tw-gap-1 tw-text-content-muted">
                  <i className="bi bi-camera"></i>
                  <Spinner size="sm">Loading...</Spinner>
                </Badge>
              )}
              {tasksLoading && <Spinner size="sm">Loading...</Spinner>}
              <Button
                variant="outline-secondary"
                size="sm"
                className="tw-h-8 tw-w-8 tw-p-0"
                onClick={() => setShowHistoryModal(true)}
                title="View history"
              >
                <i className="bi bi-clock-history"></i>
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="tw-h-8 tw-w-8 tw-p-0"
                onClick={handleExpandToggle}
                title={isExpanded ? 'Collapse details' : 'Expand details'}
                aria-expanded={isExpanded}
              >
                <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
              </Button>
            </div>
          </div>
        </Card.Header>

        {isExpanded && tasksLoading && (
          <Card.Body>
            <div className="tw-flex tw-items-center tw-justify-center tw-py-4">
              <div className="tw-text-center">
                <Spinner className="tw-mb-2 tw-text-primary">Loading...</Spinner>
                <p className="tw-mb-0 tw-text-sm tw-text-content-muted">Loading task information...</p>
              </div>
            </div>
          </Card.Body>
        )}

        {isExpanded && !tasksLoading && (
          <div className="tw-p-0">
            <FormActionBar
              form={form}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              onApprove={onApprove}
              onReject={onReject}
              onDecline={handleDecline}
              onWithdrawApproval={handleWithdrawApproval}
              onWithdrawRevisions={handleWithdrawRevisions}
              onWithdrawDecline={handleWithdrawDecline}
              hasInvalidActivity={hasInvalidActivity}
              hasIneligibleTask={hasIneligibleTask}
              hasTaskDurationAllocationConflict={hasTaskDurationAllocationConflict}
              hasZeroHours={hasZeroHours}
              availableTasks={availableTasks}
              isProcessing={isProcessing}
              processingAction={processingAction}
              isFading={isFading}
              fadingAction={fadingAction}
              showEditLabourOtBuckets={showEditLabourOtBucketsInBar}
              onEditLabourOtBuckets={handleEditLabourOtBucketsOpen}
              editLabourOtBucketsBusy={isEnsuringLabourOt}
            />
            <div className="tw-p-3">
              {viewMode === 'summary' ? (
                <FormSummaryView
                  form={form}
                  isExpanded={true}
                  onExpandToggle={() => {}}
                  availableTasks={availableTasks}
                  tasksLoading={tasksLoading}
                  validationResult={validationResult}
                  persistedLabourEfforts={persistedLabourEfforts}
                  labourOvertimeAllocations={labourOvertimeAllocations}
                  onLabourOtAllocationsRefresh={onLabourOtAllocationsRefresh}
                />
              ) : formUsesHeLineItemSummary(form.fields) ? (
                <HiredEquipmentDetailedView
                  form={form}
                  availableTasks={availableTasks}
                  onFieldsReplace={onFieldsReplace}
                  onResubmitted={onHeResubmitted}
                />
              ) : (
                <FormCompleteView
                  form={form}
                  isExpanded={true}
                  onExpandToggle={() => {}}
                  onSaveField={onSaveField}
                  availableTasks={availableTasks}
                  tasksLoading={tasksLoading}
                  validationResult={validationResult}
                  labourOvertimeAllocations={labourOvertimeAllocations}
                />
              )}
            </div>
          </div>
        )}
      </Card>

      {/* History Modal */}
      <FormHistoryModal
        form={form}
        visible={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        onAddComment={handleHistoryComment}
      />

      <ResponsiveModal
        isOpen={showLabourOtFormModal}
        onClose={() => {
          setShowLabourOtFormModal(false);
          setLabourOtModalAllocations(null);
        }}
        title="Labour DRM overtime buckets"
        size="medium"
      >
        <LabourOtFormBucketEditor
          formId={form.id}
          formStatus={form.status || ''}
          labourOvertimeAllocations={labourOtModalAllocations ?? labourOvertimeAllocations}
          onAfterSave={async () => {
            await onLabourOtAllocationsRefresh?.();
            setLabourOtModalAllocations(null);
            setShowLabourOtFormModal(false);
          }}
        />
      </ResponsiveModal>
    </>
  );
};

export default FormCard;